// Thin fetch wrapper for /api/admin/groups endpoints.
//
// Schema parity with SCIM PATCH /scim/v2/Groups/{id} — both paths write the
// same MembershipItem rows (groupName denormalized). Audit attribution
// differs by the row's `source` field ("admin" for these calls).

import { adminGet, adminPost } from "@/data/httpClient";
import { unwrapEnvelope } from "@/data/unwrapEnvelope";
import { request, ApiError } from "@/api";

export interface AdminGroupListEntry {
  group_id: string;
  group_name: string;
  member_count: number;
}

export interface AdminGroupMember {
  alias: string;
  added_at: string;
}

export interface AdminGroup {
  group_id: string;
  group_name: string;
  member_count: number;
  members: AdminGroupMember[];
}

export interface GroupClient {
  list(): Promise<{ items: AdminGroupListEntry[] }>;
  get(groupId: string): Promise<AdminGroup>;
  create(req: {
    group_id: string;
    group_name: string;
    members: string[];
  }): Promise<AdminGroup>;
  rename(
    groupId: string,
    newName: string,
  ): Promise<{ group_id: string; group_name: string; rows_updated: number }>;
  delete(
    groupId: string,
  ): Promise<{ deleted: true; rows_removed: number }>;
  addMember(groupId: string, alias: string): Promise<{ group_id: string; alias: string }>;
  removeMember(groupId: string, alias: string): Promise<{ deleted: true }>;
  /**
   * Paginated member listing — GET /api/admin/groups/{id}/members (+ ?cursor=).
   * Scalable replacement for the old /api/users full-tenant scan; reads only
   * the group's own partition. Omit `cursor` for the first page; thread the
   * returned `next_cursor` back in for subsequent pages (absent on the last).
   */
  listMembers(
    groupId: string,
    cursor?: string,
  ): Promise<{ items: AdminGroupMember[]; next_cursor?: string }>;
}

const BASE = "/api/admin/groups";

/** The single-group GET omits member_count; backfill it from members[] so
 *  callers (GroupDetailPage's `group.member_count`) don't read undefined. */
function withMemberCount(group: AdminGroup): AdminGroup {
  if (typeof group.member_count === "number") {
    return group;
  }
  return { ...group, member_count: group.members?.length ?? 0 };
}

async function adminPatchPlain<T>(path: string, body: unknown): Promise<T> {
  const res = await request<T>(path, { method: "PATCH", body });
  if (res === null) {
    throw new ApiError(0, "aborted", "request aborted", undefined, null);
  }
  return res.data;
}

async function adminDeletePlain<T>(path: string): Promise<T> {
  const res = await request<T>(path, { method: "DELETE" });
  if (res === null) {
    throw new ApiError(0, "aborted", "request aborted", undefined, null);
  }
  return res.data;
}

function createGroupClient(): GroupClient {
  return {
    list() {
      return adminGet<{ groups?: AdminGroupListEntry[]; items?: AdminGroupListEntry[] }>(
        BASE,
      ).then((resp) => ({ items: resp.groups ?? resp.items ?? [] }));
    },
    get(groupId) {
      // Enveloped as {"group": {...members}}; the single-group GET omits
      // member_count, so derive it from members[] after unwrapping.
      return adminGet<AdminGroup>(
        `${BASE}/${encodeURIComponent(groupId)}`,
      ).then((resp) => withMemberCount(unwrapEnvelope(resp, "group")));
    },
    create(req) {
      return adminPost<AdminGroup>(BASE, req).then((resp) =>
        withMemberCount(unwrapEnvelope(resp, "group")),
      );
    },
    rename(groupId, newName) {
      return adminPatchPlain(
        `${BASE}/${encodeURIComponent(groupId)}`,
        { group_name: newName },
      );
    },
    delete(groupId) {
      return adminDeletePlain(`${BASE}/${encodeURIComponent(groupId)}`);
    },
    addMember(groupId, alias) {
      return adminPost(
        `${BASE}/${encodeURIComponent(groupId)}/members`,
        { alias },
      );
    },
    removeMember(groupId, alias) {
      return adminDeletePlain(
        `${BASE}/${encodeURIComponent(groupId)}/members/${encodeURIComponent(alias)}`,
      );
    },
    listMembers(groupId, cursor) {
      const qs = cursor ? `?cursor=${encodeURIComponent(cursor)}` : "";
      return adminGet<{ items?: AdminGroupMember[]; next_cursor?: string }>(
        `${BASE}/${encodeURIComponent(groupId)}/members${qs}`,
      ).then((resp) => ({ items: resp.items ?? [], next_cursor: resp.next_cursor }));
    },
  };
}

export const groupClient: GroupClient = createGroupClient();
